import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, RefreshCw, Tablet } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useTableMenu, PublicProduct } from "@/hooks/usePublicMenu";
import { usePublicCombos, ComboWithDetails } from "@/hooks/usePublicCombos";
import { usePublicCart } from "@/hooks/usePublicCart";
import { usePublicOrder } from "@/hooks/usePublicOrder";
import { usePublicProductAddons } from "@/hooks/usePublicProductAddons";
import { PublicProductCard } from "@/components/public/PublicProductCard";
import { PublicComboCard } from "@/components/public/PublicComboCard";
import { PublicCart } from "@/components/public/PublicCart";
import { ProductDetailSheet, SelectedAddon } from "@/components/public/ProductDetailSheet";
import { PublicComboDetailSheet } from "@/components/public/PublicComboDetailSheet";
import { TableBillSection } from "@/components/public/TableBillSection";
import { CallWaiterButton } from "@/components/public/CallWaiterButton";
import { toast } from "sonner";
import type { PublicComboSelection } from "@/hooks/usePublicCart";

export default function TableMenuPage() {
  const { slug, tableNumber } = useParams<{ slug: string; tableNumber: string }>();
  const navigate = useNavigate();
  const { restaurant, categories, products, table, bill, orderItems, isLoading, error, refetchBill } = useTableMenu(slug || "", tableNumber || "");
  const { data: combos = [] } = usePublicCombos(restaurant?.id, "table");
  const cart = usePublicCart();
  const { addTableOrderItem, isSubmitting } = usePublicOrder();
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [activeTab, setActiveTab] = useState<string>("menu");
  const [selectedProduct, setSelectedProduct] = useState<PublicProduct | null>(null);
  const [productSheetOpen, setProductSheetOpen] = useState(false);
  const [selectedCombo, setSelectedCombo] = useState<ComboWithDetails | null>(null);
  const [comboSheetOpen, setComboSheetOpen] = useState(false);

  const tabletUrl = useMemo(() => {
    if (!slug) return null;
    return `/menu/${slug}/tablet`;
  }, [slug]);

  // Check if selected product has addons
  const { data: productAddons = [] } = usePublicProductAddons(selectedProduct?.id || null);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !restaurant) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">Cardápio não disponível</h1>
          <p className="text-muted-foreground">{error || "Restaurante não encontrado"}</p>
        </div>
      </div>
    );
  }

  if (!table) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">Mesa não encontrada</h1>
          <p className="text-muted-foreground">A mesa {tableNumber} não existe ou está inativa</p>
        </div>
      </div>
    );
  }

  const filteredProducts = selectedCategory === "all"
    ? products
    : products.filter((p) => p.category_id === selectedCategory);

  const getItemQuantity = (productId: string) => {
    return cart.items.filter((i) => i.id === productId).reduce((sum, i) => sum + i.quantity, 0);
  };

  const handleProductClick = async (product: PublicProduct) => {
    setSelectedProduct(product);
    setProductSheetOpen(true);
  };

  const handleAddToCart = (product: PublicProduct, quantity: number, notes: string, addons: SelectedAddon[]) => {
    cart.addItem(
      {
        id: product.id,
        name: product.name,
        price: product.price,
        image_url: product.image_url,
      },
      quantity,
      notes || undefined,
      addons.length > 0 ? addons : undefined
    );
  };

  const handleComboClick = (combo: ComboWithDetails) => {
    setSelectedCombo(combo);
    setComboSheetOpen(true);
  };

  const handleAddComboToCart = (
    combo: ComboWithDetails,
    price: number,
    selection: PublicComboSelection,
    quantity: number,
    notes: string
  ) => {
    cart.addComboItem(
      {
        id: combo.id,
        name: combo.name,
        combo_type: combo.combo_type,
        image_url: combo.image_url,
      },
      price,
      selection,
      quantity,
      notes || undefined
    );
    setComboSheetOpen(false);
  };

  const handleSimpleAdd = (product: PublicProduct) => {
    // If product has no addons, add directly; otherwise open sheet
    handleProductClick(product);
  };

  const handleSendOrder = async () => {
    if (!restaurant.customer_ordering_enabled) {
      toast.error("Pedidos pelo celular estão desativados. Chame o garçom.");
      return;
    }

    const success = await addTableOrderItem({
      restaurantId: restaurant.id,
      tableNumber: tableNumber || "",
      items: cart.items,
      comboItems: cart.comboItems,
      source: "qr",
    });

    if (success) {
      cart.clearCart();
      refetchBill();
      setActiveTab("bill");
    }
  };

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <div className="bg-primary text-primary-foreground p-4">
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          {restaurant.logo_url && (
            <img
              src={restaurant.logo_url}
              alt={restaurant.name}
              className="w-12 h-12 rounded-full bg-white object-cover"
            />
          )}
          <div className="flex-1">
            <h1 className="text-xl font-bold">{restaurant.name}</h1>
            <p className="text-sm opacity-90">Mesa {tableNumber}</p>
          </div>
          <CallWaiterButton
            restaurantId={restaurant.id}
            tableNumber={tableNumber || ""}
          />
        </div>
      </div>

      {/* QR mode notice */}
      {tabletUrl && (
        <div className="max-w-2xl mx-auto p-4">
          <Alert>
            <AlertDescription className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="font-medium">Você está no modo QR Code (celular do cliente).</p>
                <p className="text-sm text-muted-foreground">
                  Se este dispositivo for um tablet fixo na mesa, use o modo Tablet para enviar pedidos direto para a cozinha.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(tabletUrl)}
                className="sm:shrink-0"
              >
                <Tablet className="h-4 w-4 mr-2" />
                Abrir modo Tablet
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="max-w-2xl mx-auto">
        <div className="sticky top-0 bg-background border-b z-10">
          <TabsList className="w-full grid grid-cols-2">
            <TabsTrigger value="menu">Cardápio</TabsTrigger>
            <TabsTrigger value="bill">Minha Conta</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="menu" className="mt-0">
          {/* Categories */}
          <div className="sticky top-12 bg-background border-b z-10">
            <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
              <TabsList className="w-full justify-start overflow-x-auto h-auto p-1">
                <TabsTrigger value="all" className="text-xs">Todos</TabsTrigger>
                {categories.map((cat) => (
                  <TabsTrigger key={cat.id} value={cat.id} className="text-xs whitespace-nowrap">
                    {cat.name}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>

          {/* Products */}
          <div className="p-4 space-y-3">
            {/* Combos section */}
            {combos.length > 0 && (
              <div className="space-y-2">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  Combos
                </h3>
                {combos
                  .filter((c) => selectedCategory === "all" || c.category_id === selectedCategory)
                  .map((combo) => (
                    <PublicComboCard
                      key={combo.id}
                      combo={combo}
                      onClick={() => handleComboClick(combo)}
                    />
                  ))}
              </div>
            )}

            {/* Products section */}
            {filteredProducts.map((product) => (
              <PublicProductCard
                key={product.id}
                product={product}
                quantity={getItemQuantity(product.id)}
                onAdd={() => handleSimpleAdd(product)}
                onRemove={() => cart.updateQuantityById(product.id, getItemQuantity(product.id) - 1)}
              />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="bill" className="mt-0 p-4">
          <div className="flex justify-end mb-3">
            <Button variant="outline" size="sm" onClick={refetchBill}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Atualizar
            </Button>
          </div>
          <TableBillSection
            bill={bill}
            orderItems={orderItems}
            tableNumber={tableNumber || ""}
          />
        </TabsContent>
      </Tabs>

      {/* Cart Button (only on menu tab) */}
      {activeTab === "menu" && restaurant.customer_ordering_enabled && (
        <PublicCart
          items={cart.items}
          comboItems={cart.comboItems}
          subtotal={cart.subtotal}
          itemCount={cart.itemCount}
          onUpdateQuantity={cart.updateQuantity}
          onRemove={cart.removeItem}
          onUpdateComboQuantity={cart.updateComboQuantity}
          onRemoveCombo={cart.removeComboItem}
          onCheckout={handleSendOrder}
          disabled={isSubmitting}
        />
      )}

      {/* Product Detail Sheet */}
      <ProductDetailSheet
        product={selectedProduct}
        open={productSheetOpen}
        onOpenChange={setProductSheetOpen}
        onAddToCart={handleAddToCart}
      />

      {/* Combo Detail Sheet */}
      <PublicComboDetailSheet
        combo={selectedCombo}
        open={comboSheetOpen}
        onOpenChange={setComboSheetOpen}
        onAddToCart={handleAddComboToCart}
      />
    </div>
  );
}
